import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { TRANSICOES_ORCAMENTO, UFS, type OrcamentoResponse } from '@triade/contracts';
import { useCondicoesPagamento } from '@/api/condicoes-pagamento';
import { useItens } from '@/api/itens';
import { buscarPrecoInicial, useAtualizarVersao, useCriarOrcamento, useOrcamento, useOrcamentos, useSimulacaoOrcamento, useMudarSituacaoOrcamento, useVersaoOrcamento } from '@/api/orcamentos';
import { usePoliticaPreco } from '@/api/liberacao';
import { useTabelasPreco } from '@/api/tabelas-preco';
import { useTabelaEstruturada } from '@/api/destino-tributario';
import { AcaoAliquotaInterna, ehErroAliquotaInternaAusente } from '@/components/destino-tributario/acao-aliquota-interna';
import { ErroFormacao } from '@/components/formacao/avisos-formacao';
import { ItemCombobox, type ItemResumo } from '@/components/item-combobox';
import { RecordToolbar, SaidaComAlteracoesDialog, useBlockerDirty } from '@/components/record-toolbar';
import { useDebounced } from '@/hooks/use-debounced';
import { AbaVersoes, acoesDaVersao, PainelVersao, ROTULO_VERSAO } from './versoes';
import { ApiError } from '@/lib/api';
import { formatarDataHora, formatarFracaoPercentual } from '@/lib/format';
import { usePode } from '@/api/me';
import { useSessao } from '@/lib/session';
import { cn } from '@/lib/utils';
import { DadosGerais } from './dados-gerais';
import { DialogoSituacao, type DadosSituacao } from './dialogo-situacao';
import { FreteOrcamento } from './frete-orcamento';
import { ItensGrid } from './itens-grid';
import { mcPorChave } from './tipos';
import { AlertaLucro, lerAvisos, ResumoOrcamento } from './resumo-orcamento';
import { AvisosReforma } from './avisos-reforma';
import { ParametrosCongelados } from './parametros-congelados';
import { BotaoRelatorios } from '@/relatorios';
import { camposAlterados, decimalTexto, deResposta, formVazio, hoje, novaChave, paraDto, ROTULO_CATEGORIA, ROTULO_STATUS, type FormOrcamento } from './tipos';

function mensagemErro(e: unknown): string {
  return e instanceof ApiError ? `${e.message}${e.code ? ` (${e.code})` : ''}` : 'falha de comunicação com a API';
}

/** Tela "Orçamento — detalhe" (/vender/orcamentos/$id?versao=N e /vender/orcamentos/novo). Sem `versao`, mostra a versao exibida (rascunho aberto, senao a de referencia). */
export function OrcamentoPage({ id, versao, guia }: { id?: string; versao?: number; guia?: 'versoes' }) {
  const { empresaId } = useSessao();
  const orc = useOrcamento(id);
  // Versao pedida na URL: a propria resposta do detalhe quando for a exibida; senao GET /orcamentos/:id/versoes/:n.
  const pedida = versao !== undefined && versao !== orc.data?.versaoNumero ? versao : undefined;
  const outra = useVersaoOrcamento(id, pedida);
  const exibida = pedida === undefined ? orc.data : outra.data;

  let corpo;
  if (empresaId === null) corpo = <Vazio>Escolha a empresa no menu lateral para operar.</Vazio>;
  else if (id && orc.isError) corpo = <ErroFormacao erro={orc.error} onTentar={() => void orc.refetch()} />;
  else if (id && pedida !== undefined && outra.isError) corpo = <ErroFormacao erro={outra.error} onTentar={() => void outra.refetch()} />;
  else if (id && (!orc.data || !exibida)) corpo = <Vazio papel="status">Carregando orçamento…</Vazio>;
  else corpo = <Editor key={id ? `${id}-${exibida?.versaoId}` : 'novo'} orc={exibida ?? null} detalhe={orc.data ?? null} guia={guia} />;

  return <div className="flex flex-col gap-5 pb-4">{corpo}</div>;
}

/** `orc` e a versao exibida (o formulario edita o rascunho dela); `detalhe` traz o cabecalho, `versoes` e o historico. */
function Editor({ orc, detalhe, guia }: { orc: OrcamentoResponse | null; detalhe: OrcamentoResponse | null; guia?: 'versoes' }) {
  const navigate = useNavigate();
  const base = useMemo<FormOrcamento>(() => (orc ? deResposta(orc) : formVazio(hoje())), [orc]);
  const [form, setForm] = useState<FormOrcamento>(base);
  const [salvoEm, setSalvoEm] = useState<Date | null>(null);
  const [erroSalvar, setErroSalvar] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [erroItem, setErroItem] = useState<string | null>(null);
  const [adicionando, setAdicionando] = useState(false);
  const [destino, setDestino] = useState<OrcamentoResponse['status'] | null>(null);
  const [criadoId, setCriadoId] = useState<string | null>(null);
  const [termo, setTermo] = useState('');
  const termoBusca = useDebounced(termo.trim(), 250);
  // Politica NEGOCIADO: o preco inicial de cada item vem do sugerido (aviso discreto abaixo da busca).
  const negociado = usePoliticaPreco().data?.politicaPreco === 'NEGOCIADO';

  // Servidor mudou (salvou, aprovou, reabriu): o formulario volta a refletir o gravado.
  useEffect(() => setForm(base), [base]);

  // Rascunho editavel so por quem tem orcamento.editar; sem custo.ver a API omite o custo do frete e a tela nem o pede.
  const { pode } = usePode();
  const podeEditar = pode('orcamento.editar');
  const comCusto = pode('custo.ver');
  // Sem lucro.ver a API omite lucro, meta, minimo e MC: o orcamento mostra so a cor da faixa.
  const comLucro = pode('lucro.ver');
  // Editavel = a VERSAO exibida e rascunho (uma enviada e congelada, mesmo com o cabecalho em ENVIADO).
  const editavel = (orc === null || orc.versaoStatus === 'RASCUNHO') && podeEditar;
  const itens = useItens({ q: termoBusca || undefined, ativo: true, comerciavel: true, limit: 20 });
  const tabelas = useTabelasPreco({ limit: 200 });
  const condicoes = useCondicoesPagamento({ limit: 200 });
  // Sessao 51: o contribuinte de ICMS so aparece quando a tabela do orcamento e ESTRUTURADA na data.
  const destinoEstruturado = useTabelaEstruturada(form.tabelaId || undefined, form.data) === true;
  // Posicao ("n de total") e vizinhos vem da API: total real do filtro e consultas de 1 registro por numero.
  const numero = orc?.numero;
  const posicao = useOrcamentos({ numeroAte: numero, limit: 1 }, { ativo: numero !== undefined });
  const todos = useOrcamentos({ limit: 1 });
  const vizAnterior = useOrcamentos(numero === undefined ? { limit: 1 } : { numeroAte: Math.max(numero - 1, 1), limit: 1 }, { ativo: numero !== 1 });
  const vizProximo = useOrcamentos({ numeroDe: (numero ?? 0) + 1, ordenar: 'numero', direcao: 'asc', limit: 1 }, { ativo: numero !== undefined });

  const alterados = camposAlterados(form, base);
  const dirty = editavel && alterados.length > 0;
  const bloqueio = useBlockerDirty(dirty && criadoId === null);

  const { dto, problema } = useMemo(() => paraDto(form, comCusto), [form, comCusto]);
  const dtoKey = dto ? JSON.stringify(dto) : null;
  const dtoDebounced = useDebounced(dtoKey, 400);
  const dtoSimular = useMemo(() => (dtoDebounced ? (JSON.parse(dtoDebounced) as NonNullable<typeof dto>) : null), [dtoDebounced]);
  // Recalcula na API a cada edicao; orcamento gravado e sem alteracoes usa o que a API ja devolveu.
  const usaSimulacao = editavel && (orc === null || dirty);
  const simulacao = useSimulacaoOrcamento(usaSimulacao ? dtoSimular : null);
  const sincronizado = dtoKey !== null && dtoKey === dtoDebounced;
  const atual = !usaSimulacao || (sincronizado && simulacao.isSuccess && !simulacao.isFetching);

  const exibicao = usaSimulacao ? (simulacao.data?.exibicao ?? orc?.exibicao) : orc?.exibicao;
  const avisos = lerAvisos(usaSimulacao ? (simulacao.data?.warnings ?? orc?.warnings) : orc?.warnings);
  // MC so do orcamento gravado: a simulacao nao devolve MC; com alteracoes nao gravadas a MC gravada ficaria desatualizada.
  const mcGravada = useMemo(() => (usaSimulacao || !orc ? undefined : mcPorChave(orc.itens)), [usaSimulacao, orc]);
  const mcTotal = usaSimulacao ? undefined : orc?.mc;

  const criar = useCriarOrcamento();
  const atualizar = useAtualizarVersao();
  const mudarSituacao = useMudarSituacaoOrcamento();
  const salvando = criar.isPending || atualizar.isPending;

  useEffect(() => {
    if (criadoId) void navigate({ to: '/vender/orcamentos/$id', params: { id: criadoId } });
  }, [criadoId, navigate]);

  const set = useCallback(<K extends keyof FormOrcamento>(campo: K, valor: FormOrcamento[K]) => setForm((f) => ({ ...f, [campo]: valor })), []);
  const alterarLinha = useCallback(
    (chave: string, campo: 'quantidade' | 'preco', valor: string) =>
      setForm((f) => ({ ...f, linhas: f.linhas.map((l) => (l.chave === chave ? { ...l, [campo]: valor } : l)) })),
    [],
  );
  const removerLinha = useCallback((chave: string) => setForm((f) => ({ ...f, linhas: f.linhas.filter((l) => l.chave !== chave) })), []);

  async function adicionar(it: ItemResumo) {
    setErroItem(null);
    if (!form.tabelaId || !form.condicaoId) {
      setErroItem('Escolha a tabela de preço e a condição de pagamento antes de adicionar itens.');
      return;
    }
    setAdicionando(true);
    try {
      const r = await buscarPrecoInicial({
        itemId: it.id,
        codigo: it.codigo,
        tabelaId: form.tabelaId,
        condicaoId: form.condicaoId,
        data: form.data,
        politica: negociado ? 'NEGOCIADO' : 'TABELA',
        // UF e contribuinte escolhidos no orcamento: o preco inicial e o da UF de destino (a API calcula; a tela so repassa).
        ufDestino: UFS.find((u) => u === form.ufDestino),
        contribuinte: form.contribuinte,
      });
      if ('pulado' in r) {
        setErroItem(`Item ${it.codigo} não pode ser precificado: ${r.pulado}`);
        return;
      }
      const unidade = itens.data?.find((x) => x.id === it.id)?.unidade ?? '';
      setForm((f) => ({
        ...f,
        linhas: [...f.linhas, { chave: novaChave(), itemId: it.id, codigo: it.codigo, descricao: it.descricao, unidade, quantidade: '1', preco: decimalTexto(r.preco) }],
      }));
    } catch (e) {
      setErroItem(`Não foi possível buscar o preço do item ${it.codigo}: ${mensagemErro(e)}`);
    } finally {
      setAdicionando(false);
    }
  }

  async function salvar() {
    if (!dirty || !dto) return;
    setErroSalvar(null);
    setAviso(null);
    try {
      if (orc) {
        await atualizar.mutateAsync({ id: orc.id, n: orc.versaoNumero, ...dto });
        setSalvoEm(new Date());
      } else {
        const criado = await criar.mutateAsync(dto);
        setSalvoEm(new Date());
        setCriadoId(criado.id);
      }
    } catch (e) {
      setErroSalvar(mensagemErro(e));
    }
  }

  /** Chama a API; se falhar, o dialogo continua aberto e mostra o erro (ex.: 422 MOTIVO_OBRIGATORIO). */
  async function mudar(dados: DadosSituacao) {
    if (!orc || !destino) return;
    await mudarSituacao.mutateAsync({ id: orc.id, para: destino, ...dados });
    setDestino(null);
  }

  // Navegacao anterior/proximo: vizinhos por numero, buscados na API.
  const anterior = vizAnterior.data?.itens[0];
  const proximo = vizProximo.data?.itens[0];
  const indice = orc ? posicao.data?.total : undefined;
  const totalGeral = todos.data?.total;
  const abrir = (o: OrcamentoResponse | undefined) => {
    if (o) void navigate({ to: '/vender/orcamentos/$id', params: { id: o.id } });
  };

  const status = orc?.status ?? 'RASCUNHO';
  /** Troca a versao exibida (`?versao=N`) ou a aba (`?guia=versoes`), preservando o resto da URL. */
  const irPara = (mudanca: { versao?: number; guia?: 'versoes' }) => {
    if (!orc) return;
    void navigate({ to: '/vender/orcamentos/$id', params: { id: orc.id }, search: (prev) => ({ ...prev, versao: mudanca.versao, guia: mudanca.guia }) });
  };
  // ENVIADO e APROVADO saem pelas rotas de versao (painel de versoes), nunca pelo atalho POST :id/situacao.
  const destinos = orc && podeEditar ? TRANSICOES_ORCAMENTO[orc.status].filter((d) => d === 'EM_NEGOCIACAO' || d === 'PERDIDO') : [];
  const acaoPendente = mudarSituacao.isPending;

  return (
    <>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-small text-ink-muted">
            Vender <span aria-hidden="true">/</span>{' '}
            <Link to="/vender/orcamentos" className="text-ink-muted hover:underline">
              Orçamentos
            </Link>{' '}
            <span aria-hidden="true">/</span> {orc ? orc.numeroExibido : 'Novo'}
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-3">
            <h1 className="text-display font-semibold tracking-[-0.01em]">{orc ? `Orçamento ${orc.numeroExibido}` : 'Novo orçamento'}</h1>
            <span data-testid="status-orcamento" className="rounded-full border border-border bg-line-soft px-2.5 py-0.5 text-caption font-semibold text-ink-body">
              {ROTULO_STATUS[status]}
            </span>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3">
        {orc && destinos.length > 0 && (
          <div className="flex items-center gap-2">
            <label htmlFor="o-situacao" className="text-small text-ink-secondary">
              Situação
            </label>
            <select
              id="o-situacao"
              value={orc.status}
              disabled={dirty || acaoPendente}
              title={dirty ? 'Salve as alterações antes de mudar a situação' : undefined}
              onChange={(e) => setDestino(e.target.value as OrcamentoResponse['status'])}
              className="h-[38px] rounded-[10px] border border-input bg-card px-3 font-medium outline-none focus:border-primary focus:shadow-focus disabled:opacity-50"
            >
              <option value={orc.status}>{ROTULO_STATUS[orc.status]}</option>
              {destinos.map((d) => (
                <option key={d} value={d}>
                  {ROTULO_STATUS[d]}
                </option>
              ))}
            </select>
          </div>
        )}
        {orc && <BotaoRelatorios tela="orcamento" params={{ orcamentoId: orc.id }} />}
        </div>
      </div>
      <Etapas status={status} historico={detalhe?.situacoes ?? []} />

      {orc && detalhe && (
        <PainelVersao
          detalhe={detalhe}
          ver={orc}
          podeEditar={podeEditar}
          alteracoesPendentes={dirty}
          onSelecionar={(n) => irPara({ versao: n })}
        />
      )}

      {orc && detalhe && (
        <div role="tablist" aria-label="Seções do orçamento" className="flex gap-1 border-b border-border">
          {(
            [
              { id: 'orcamento', rotulo: 'Orçamento', ativa: guia !== 'versoes', mudar: () => irPara({ guia: undefined }) },
              { id: 'versoes', rotulo: `Versões (${detalhe.versoes.length})`, ativa: guia === 'versoes', mudar: () => irPara({ versao: orc.versaoNumero, guia: 'versoes' }) },
            ] as const
          ).map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              id={`aba-${t.id}`}
              aria-selected={t.ativa}
              data-testid={`aba-${t.id}`}
              onClick={t.mudar}
              className={cn('-mb-px border-b-2 px-4 py-2 text-small font-medium', t.ativa ? 'border-primary text-ink' : 'border-transparent text-ink-muted hover:text-ink')}
            >
              {t.rotulo}
            </button>
          ))}
        </div>
      )}

      {!editavel && (
        <div role="status" data-testid="aviso-somente-leitura" className="rounded-xl border border-border bg-line-soft px-4 py-3 text-small text-ink-body">
          <b>Somente leitura.</b>{' '}
          {orc ? `Versão ${orc.versaoNumero} ${ROTULO_VERSAO[orc.versaoStatus].toLowerCase()}` : `Orçamento ${ROTULO_STATUS[status].toLowerCase()}`}
          {orc && detalhe ? `; situação ${ROTULO_STATUS[status].toLowerCase()} desde ${formatarDataHora(detalhe.statusAlteradoEm)}` : ''}:{' '}
          {podeEditar
            ? 'só o rascunho pode ser editado. Para mudar valores de uma versão enviada, crie a próxima versão.'
            : 'seu acesso é de consulta.'}
          {detalhe?.motivoPerdido && (
            <span data-testid="motivo-perdido" className="mt-1 block">
              <b>Motivo da perda:</b> {detalhe?.motivoPerdidoCategoria ? `${ROTULO_CATEGORIA[detalhe.motivoPerdidoCategoria]}. ` : ''}
              {detalhe?.motivoPerdido}
            </span>
          )}
        </div>
      )}
      {aviso && (
        <div role="status" className="rounded-xl border border-border bg-line-soft px-4 py-3 text-small text-ink-body">
          {aviso}
        </div>
      )}

      {guia === 'versoes' && orc && detalhe ? (
        <AbaVersoes detalhe={detalhe} numeroAtual={orc.versaoNumero} onVer={(n: number) => irPara({ versao: n })} />
      ) : (
      <div className="flex flex-wrap items-start gap-5">
        <div className="flex min-w-0 flex-[999_1_560px] flex-col gap-5">
          <DadosGerais form={form} set={set} editavel={editavel} tabelas={tabelas} condicoes={condicoes} reforma={exibicao?.reforma} destinoEstruturado={destinoEstruturado} />

          <section aria-label="Itens" className="rounded-2xl border border-border bg-card">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-5 py-4">
              <h2 className="text-title font-semibold">Itens</h2>
              {editavel && (
                <div className="w-full max-w-[360px] flex-[0_1_360px]">
                  <ItemCombobox
                    ariaLabel="Adicionar item"
                    placeholder="Adicionar produto ou serviço pelo código ou nome"
                    valor={null}
                    onSelecionar={(it) => void adicionar(it)}
                    itens={itens.data ?? []}
                    carregando={itens.isFetching}
                    erro={itens.error?.message ?? null}
                    termo={termo}
                    onTermoChange={setTermo}
                    disabled={adicionando}
                  />
                  {negociado && (
                    <p data-testid="aviso-negociado" className="mt-1 text-caption text-ink-muted">
                      Política negociado: o preço inicial parte do sugerido.
                    </p>
                  )}
                </div>
              )}
            </div>
            {erroItem && (
              <p role="alert" className="border-b border-border px-5 py-3 text-small text-danger-ink">
                {erroItem}
              </p>
            )}
            {form.linhas.length === 0 ? (
              <p className="px-5 py-8 text-center text-small text-ink-muted">
                {editavel ? 'Nenhum item ainda. Busque um produto ou serviço acima para adicionar.' : 'Este orçamento não tem itens.'}
              </p>
            ) : (
              <div className={cn(usaSimulacao && !atual && 'opacity-80')} aria-busy={usaSimulacao && !atual}>
                <ItensGrid
                  linhas={form.linhas}
                  exibicao={exibicao}
                  atual={atual}
                  editavel={editavel}
                  onAlterar={alterarLinha}
                  onRemover={removerLinha}
                  mcPorChave={mcGravada}
                  comLucro={comLucro}
                />
              </div>
            )}
            <div className="flex flex-wrap gap-4 border-t border-border px-5 py-3 text-caption text-ink-secondary">
              {comLucro ? (
                <>
                  <Legenda cor="bg-success">Na meta (≥ {formatarFracaoPercentual(exibicao?.metaLucro)})</Legenda>
                  <Legenda cor="bg-warning-dot">Abaixo da meta, acima do mínimo</Legenda>
                  <Legenda cor="bg-destructive">Abaixo do mínimo ({formatarFracaoPercentual(exibicao?.minimoLucro)})</Legenda>
                </>
              ) : (
                <>
                  <Legenda cor="bg-success">Na meta</Legenda>
                  <Legenda cor="bg-warning-dot">Abaixo da meta</Legenda>
                  <Legenda cor="bg-destructive">Abaixo do mínimo</Legenda>
                </>
              )}
            </div>
            {editavel && problema && (
              <p role="status" className="border-t border-border px-5 py-3 text-caption text-ink-muted">
                Para calcular: {problema}
              </p>
            )}
          </section>

          <FreteOrcamento form={form} set={set} editavel={editavel} comCusto={comCusto} />
        </div>

        <aside aria-label="Resumo" className="flex min-w-0 flex-[1_1_300px] flex-col gap-4">
          {usaSimulacao && simulacao.isError && (
            <ErroFormacao erro={simulacao.error} onTentar={() => void simulacao.refetch()} acao={ehErroAliquotaInternaAusente(simulacao.error) ? <AcaoAliquotaInterna /> : undefined} />
          )}
          <ResumoOrcamento exibicao={exibicao} consideraFreteLucro={form.consideraFreteLucro} carregando={usaSimulacao && !!dtoKey && !atual && !simulacao.isError} mc={mcTotal} />
          <AlertaLucro exibicao={exibicao} avisos={avisos} />
          <AvisosReforma avisos={orc?.avisosReforma} reformaAtual={exibicao?.reforma} dto={dto} />
          {orc && <ParametrosCongelados orc={orc} podeAtualizar={podeEditar && comCusto} alteracoesPendentes={dirty} />}
          {detalhe && <HistoricoSituacoes historico={detalhe.situacoes} />}
        </aside>
      </div>
      )}

      <RecordToolbar
        dirty={dirty}
        saving={salvando}
        savedAt={salvoEm}
        erro={erroSalvar}
        novo={orc === null}
        index={indice}
        total={totalGeral}
        canPrev={anterior !== undefined && !dirty}
        canNext={proximo !== undefined && !dirty}
        alteradosCount={dirty ? alterados.length : undefined}
        onList={() => void navigate({ to: '/vender/orcamentos' })}
        onSave={() => void salvar()}
        onNew={() => {
          if (orc) void navigate({ to: '/vender/orcamentos/novo' });
          else setForm(formVazio(hoje()));
        }}
        onUndo={() => {
          setForm(base);
          setErroSalvar(null);
        }}
        onPrev={() => abrir(anterior)}
        onNext={() => abrir(proximo)}
        onDelete={() =>
          setAviso(
            orc
              ? 'Orçamentos não podem ser excluídos: o registro é imutável. Para deixar de usá-lo, marque-o como Perdido.'
              : 'Este orçamento ainda não foi salvo. Para descartar as alterações, use Desfazer.',
          )
        }
      />
      <SaidaComAlteracoesDialog {...bloqueio} />

      <DialogoSituacao numero={orc?.numero ?? 0} para={destino} onConfirmar={mudar} onCancelar={() => setDestino(null)} />
    </>
  );
}

const FLUXO: Array<OrcamentoResponse['status']> = ['RASCUNHO', 'ENVIADO', 'EM_NEGOCIACAO', 'APROVADO'];

/** Etapas do design; "Perdido" aparece (em vermelho) so quando o orcamento foi perdido. Etapas ja percorridas vem do historico. */
function Etapas({ status, historico }: { status: OrcamentoResponse['status']; historico: OrcamentoResponse['situacoes'] }) {
  const percorridas = new Set<string>(['RASCUNHO', ...historico.map((h) => h.para)]);
  const passos = status === 'PERDIDO' ? [...FLUXO, 'PERDIDO' as const] : FLUXO;
  return (
    <ol aria-label="Etapas do orçamento" className="m-0 flex list-none flex-wrap gap-2 p-0">
      {passos.map((p, i) => {
        const ativo = p === status;
        const perdido = p === 'PERDIDO';
        return (
          <li
            key={p}
            aria-current={ativo ? 'step' : undefined}
            data-etapa={p}
            className={cn(
              'flex items-center gap-2 rounded-full border bg-card py-1.5 pr-3 pl-1.5 text-small',
              ativo ? (perdido ? 'border-destructive font-semibold' : 'border-primary font-semibold') : percorridas.has(p) ? 'border-border text-ink-body' : 'border-border text-ink-secondary',
            )}
          >
            <span
              className={cn(
                'flex size-[22px] items-center justify-center rounded-full text-caption',
                ativo ? (perdido ? 'bg-destructive text-white' : 'bg-primary text-primary-foreground') : 'bg-line-soft',
              )}
            >
              {i + 1}
            </span>
            {ROTULO_STATUS[p]}
          </li>
        );
      })}
    </ol>
  );
}

/** Linha do tempo das situacoes (GET /orcamentos/:id -> situacoes), do mais antigo ao mais novo. So exibe o que a API gravou. */
function HistoricoSituacoes({ historico }: { historico: OrcamentoResponse['situacoes'] }) {
  return (
    <section aria-label="Histórico de situações" className="rounded-2xl border border-border bg-card px-[18px] py-4 text-small">
      <h2 className="font-semibold text-ink">Histórico de situações</h2>
      <ol className="mt-2 flex list-none flex-col gap-3 p-0">
        {historico.map((h) => (
          <li key={h.id} data-testid="historico-item" data-para={h.para} className="border-l-2 border-border pl-3">
            <div className="font-medium text-ink">
              {h.de ? `${ROTULO_STATUS[h.de]} → ${ROTULO_STATUS[h.para]}` : `Criado como ${ROTULO_STATUS[h.para].toLowerCase()}`}
            </div>
            <div className="text-caption text-ink-muted">
              {formatarDataHora(h.createdAt)}
              {h.usuarioNome ? ` · ${h.usuarioNome}` : ''}
            </div>
            {(h.categoria || h.motivo) && (
              <p className="mt-0.5 text-ink-body">
                {h.categoria ? <b>{ROTULO_CATEGORIA[h.categoria]}: </b> : null}
                {h.motivo}
              </p>
            )}
          </li>
        ))}
      </ol>
    </section>
  );
}

function Legenda({ cor, children }: { cor: string; children: React.ReactNode }) {
  return (
    <span>
      <span aria-hidden="true" className={cn('mr-1.5 inline-block size-2 rounded-full', cor)} />
      {children}
    </span>
  );
}

function Vazio({ children, papel }: { children: React.ReactNode; papel?: 'status' }) {
  return (
    <p role={papel} className="rounded-2xl border border-dashed border-border bg-card px-5 py-10 text-center text-small text-ink-muted">
      {children}
    </p>
  );
}

